import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { resolveInvoicePresignedUrl } from "../../PharmacySales/NewSales/utils/invoiceUrlUtils";

const InvoicePreviewModal = ({
	isOpen,
	toggle,
	invoiceUrl,
	invoiceNo,
	title,
}) => {
	const { t } = useTranslation();
	const iframeRef = useRef(null);
	const [presignedUrl, setPresignedUrl] = useState("");
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const modalTitle = title || t("Invoice Preview");

	useEffect(() => {
		if (!isOpen || !invoiceUrl) {
			setPresignedUrl("");
			setLoadError("");
			return undefined;
		}

		let cancelled = false;

		const loadPresignedUrl = async () => {
			setLoading(true);
			setLoadError("");
			setPresignedUrl("");

			try {
				// Parent may already pass a resolved presigned URL
				const url = await resolveInvoicePresignedUrl(invoiceUrl);
				if (cancelled) return;

				if (!url) {
					setLoadError(t("Preview is not available."));
					return;
				}

				setPresignedUrl(url);
			} catch (error) {
				if (cancelled) return;
				setLoadError(
					error?.response?.data?.message ||
						t("Failed to load preview. Please try again.")
				);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadPresignedUrl();

		return () => {
			cancelled = true;
		};
	}, [isOpen, invoiceUrl, t]);

	const handleDownload = () => {
		if (!presignedUrl) return;

		const link = document.createElement("a");
		link.href = presignedUrl;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		link.download = "";
		link.style.display = "none";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handlePrint = () => {
		if (iframeRef.current?.contentWindow) {
			iframeRef.current.contentWindow.focus();
			iframeRef.current.contentWindow.print();
			return;
		}

		if (presignedUrl) {
			const printWindow = window.open(presignedUrl, "_blank", "noopener,noreferrer");
			printWindow?.focus();
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={toggle}
			size="xl"
			centered
			scrollable
			backdrop="static"
			keyboard={false}
		>
			<ModalHeader toggle={toggle}>
				{modalTitle}
				{invoiceNo ? ` - ${invoiceNo}` : ""}
			</ModalHeader>
			<ModalBody className="p-0">
				{loading && (
					<div className="d-flex justify-content-center align-items-center p-5">
						<Spinner size="sm" className="me-2" />
						<span>{t("Loading...")}</span>
					</div>
				)}
				{!loading && loadError && (
					<div className="text-center p-5 text-muted">{loadError}</div>
				)}
				{!loading && !loadError && !presignedUrl && !invoiceUrl && (
					<div className="text-center p-5 text-muted">
						{t("Preview is not available.")}
					</div>
				)}
				{!loading && presignedUrl && (
					<iframe
						ref={iframeRef}
						src={presignedUrl}
						title={modalTitle}
						style={{ width: "100%", minHeight: "70vh", border: "none" }}
					/>
				)}
			</ModalBody>
			<ModalFooter className="d-flex flex-wrap gap-2 justify-content-end">
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleDownload}
					disabled={!presignedUrl}
				>
					<i className="fa fa-expand me-2" />
					{t("Fullscreen")}
				</button>
				{/* <button
					type="button"
					className="btn btn-secondary"
					onClick={handlePrint}
					disabled={!presignedUrl}
				>
					<i className="fa fa-print me-2" />
					{t("Print")}
				</button> */}
				<button type="button" className="btn btn-light" onClick={toggle}>
					{t("Close")}
				</button>
			</ModalFooter>
		</Modal>
	);
};

export default InvoicePreviewModal;
